import type { ReactNode } from "react";
import { Link } from "react-router";
import { useGetOne } from "ra-core";
import {
  Bell,
  CalendarDays,
  Check,
  Clock,
  Flag,
  FolderKanban,
  Pencil,
  RotateCcw,
  User,
  UserRound,
  Users,
} from "lucide-react";
import type {
  Contact,
  OrganizationMember,
  Task,
} from "@/components/atomic-crm/types";
import { Button } from "@/components/ui/button";
import { TaskAssignedAvatars } from "@/components/atomic-crm/tasks/TaskAssignedAvatars";
import { TaskMentionText } from "@/components/atomic-crm/tasks/TaskMentionText";
import { getMemberName } from "@/components/atomic-crm/tasks/taskMemberOptions";
import { useTaskCompletionToggle } from "@/components/atomic-crm/tasks/useTaskCompletionToggle";
import { useTaskParticipantsByTaskIds } from "@/components/atomic-crm/tasks/useTaskParticipants";
import {
  formatEventTimeRange,
  getContactDisplayName,
} from "@/modules/calendar/calendarReminderOptions";
import { formatReminderOffsetsSummary } from "@/modules/calendar/calendarReminderWriteUtils";
import {
  isCalendarEntryEvent,
  parseDateKey,
  toDateKey,
} from "@/modules/calendar/calendarUtils";
import { getWorkCategoryMeta } from "@/modules/work/workCategoryUtils";
import type { WorkItem } from "@/modules/work/workTypes";
import { cn } from "@/lib/utils";

const longDate = (dateKey: string) =>
  parseDateKey(dateKey).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const shortDate = (dateKey: string) =>
  parseDateKey(dateKey).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const formatWhen = (item: WorkItem) => {
  const dates =
    item.startDate && item.startDate < item.date
      ? `${shortDate(item.startDate)} → ${longDate(item.date)}`
      : longDate(item.date);
  const time = isCalendarEntryEvent(item.event)
    ? formatEventTimeRange(
        item.event.record.event_time,
        item.event.record.duration_minutes,
      )
    : item.time;
  return time ? `${dates} · ${time}` : dates;
};

const DetailRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof CalendarDays;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-2.5 text-sm">
    <Icon
      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
      aria-hidden
    />
    <span className="sr-only">{label}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const ContactName = ({ contactId }: { contactId: Task["contact_id"] }) => {
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: contactId ?? "" },
    { enabled: contactId != null },
  );
  if (!contact) return null;
  return (
    <DetailRow icon={UserRound} label="Contact">
      <Link
        to={`/contacts/${contact.id}/show`}
        className="truncate link-action"
      >
        {getContactDisplayName(contact) || "Contact"}
      </Link>
    </DetailRow>
  );
};

const MemberName = ({
  memberId,
  prefix,
}: {
  memberId: OrganizationMember["id"] | null | undefined;
  prefix: string;
}) => {
  const { data: member } = useGetOne<OrganizationMember>(
    "organization_members",
    { id: memberId ?? "" },
    { enabled: memberId != null },
  );
  if (!member) return null;
  return (
    <DetailRow icon={User} label={prefix}>
      <span className="text-muted-foreground">{prefix} </span>
      {getMemberName(member)}
    </DetailRow>
  );
};

const statusOf = (item: WorkItem) => {
  if (item.done)
    return { label: "Done", className: "bg-success/15 text-success" };
  if (item.overdue)
    return {
      label: "Overdue",
      className: "bg-destructive/10 text-destructive",
    };
  const todayKey = toDateKey(new Date());
  if (item.startDate && item.startDate <= todayKey)
    return { label: "In progress", className: "bg-primary/10 text-primary" };
  return null;
};

const TaskActions = ({ task, onEdit }: { task: Task; onEdit: () => void }) => {
  const { participantsByTaskId } = useTaskParticipantsByTaskIds([task.id]);
  const { toggle, checkboxChecked, checkboxDisabled, isPending } =
    useTaskCompletionToggle(task, participantsByTaskId[String(task.id)] ?? []);

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={checkboxDisabled || isPending}
        onClick={() => toggle()}
      >
        {checkboxChecked ? (
          <RotateCcw className="size-4" />
        ) : (
          <Check className="size-4" />
        )}
        {checkboxChecked ? "Reopen" : "Mark done"}
      </Button>
      <Button type="button" size="sm" onClick={onEdit}>
        <Pencil className="size-4" />
        Edit
      </Button>
    </>
  );
};

/**
 * Read-only summary of a calendar / timeline item: what it is, when, who and
 * where it belongs, with Edit as an explicit action.
 */
export const WorkItemSummary = ({
  item,
  onEdit,
}: {
  item: WorkItem;
  onEdit: (item: WorkItem) => void;
}) => {
  const category = getWorkCategoryMeta(item.category);
  const status = statusOf(item);
  const event = item.event;
  const task = event.kind === "task" ? event.task : null;
  const record = isCalendarEntryEvent(event) ? event.record : null;
  const isProjectMilestone =
    event.kind === "project_delivery" || event.kind === "project_start";
  const reminders = task?.reminder_offsets_minutes?.length
    ? formatReminderOffsetsSummary(task.reminder_offsets_minutes)
    : record
      ? formatReminderOffsetsSummary(
          record.reminder_offsets_minutes ??
            (record.remind_before_minutes != null
              ? [record.remind_before_minutes]
              : []),
        )
      : null;

  return (
    <div className="w-80">
      <div className="space-y-2 border-b p-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <span className={cn("size-2 rounded-full", category.dotClass)} />
            {event.kind === "project_start" ? "Project start" : category.label}
          </span>
          {status ? (
            <span
              className={cn(
                "rounded-sm px-1.5 py-0.5 text-2xs font-medium",
                status.className,
              )}
            >
              {status.label}
            </span>
          ) : null}
          {task?.priority === "high" ? (
            <span className="ml-auto inline-flex items-center gap-1 text-2xs font-medium text-destructive">
              <Flag className="size-3 fill-current" />
              High
            </span>
          ) : null}
        </div>
        <div
          className={cn(
            "text-sm font-semibold leading-snug break-words",
            item.done && "text-muted-foreground line-through",
          )}
        >
          {task ? <TaskMentionText text={task.text} /> : item.title}
        </div>
      </div>

      <div className="space-y-2.5 p-4">
        <DetailRow icon={CalendarDays} label="When">
          {formatWhen(item)}
        </DetailRow>

        {item.dealId != null && item.dealName ? (
          <DetailRow icon={FolderKanban} label="Project">
            <Link
              to={`/deals/${item.dealId}/show`}
              className="truncate link-action"
            >
              {item.dealName}
            </Link>
          </DetailRow>
        ) : null}

        {task?.contact_id != null || record?.contact_id != null ? (
          <ContactName contactId={task?.contact_id ?? record?.contact_id} />
        ) : null}

        {task ? (
          <DetailRow icon={Users} label="Team">
            <TaskAssignedAvatars task={task} />
          </DetailRow>
        ) : event.kind !== "task" &&
          "assignedName" in event &&
          event.assignedName ? (
          <DetailRow icon={Users} label="Team">
            {event.assignedName}
          </DetailRow>
        ) : null}

        {task ? (
          <MemberName memberId={task.organization_member_id} prefix="Owner" />
        ) : record?.organization_member_id != null ? (
          <MemberName memberId={record.organization_member_id} prefix="Owner" />
        ) : null}

        {reminders ? (
          <DetailRow icon={Bell} label="Reminders">
            {reminders}
          </DetailRow>
        ) : null}

        {task?.created_at || record?.created_at ? (
          <DetailRow icon={Clock} label="Created">
            <span className="text-muted-foreground">
              Created{" "}
              {new Date(
                (task?.created_at ?? record?.created_at)!,
              ).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </DetailRow>
        ) : null}

        {record?.description ? (
          <p className="line-clamp-4 border-t pt-2.5 text-xs text-muted-foreground">
            {record.description}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t bg-muted/30 px-4 py-3">
        {task ? (
          <TaskActions task={task} onEdit={() => onEdit(item)} />
        ) : isProjectMilestone ? (
          <Button type="button" size="sm" onClick={() => onEdit(item)}>
            <FolderKanban className="size-4" />
            Open project
          </Button>
        ) : (
          <Button type="button" size="sm" onClick={() => onEdit(item)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </div>
    </div>
  );
};
